// ops_vsm (vsm_board) table behaviors, extracted for the legacy
// vehicle_status_* pages: status chip-buttons with an anchored picker
// popover, the status-history chip rows, the notes save flash, and the
// 60s soft auto-refresh with its header toggle.
//
// Pages provide the endpoints and data plumbing:
//   applyStatusChange(item, field, value)  save one status change
//   opsSoftReload()                        quiet re-fetch + merge (no spinner)
// and use vsmMergeRows() inside their reload so open history rows and a
// focused notes input survive the refresh.
import { statusLabel } from '@/utils/vehicle_status_labels'

// chip color per status, mirroring vsm_board's WASH/MAINT_CHIP_CLASSES
// (legacy pages also carry OOS as a wash status, which the board does not)
const WASH_CHIP_CLASSES = {
  READY: 'vst-chip--green',
  CLEANED: 'vst-chip--cyan',
  WASHED: 'vst-chip--teal',
  DIRTY: 'vst-chip--brown',
  OUT: 'vst-chip--purple',
  OOS: 'vst-chip--red',
}

const MAINT_CHIP_CLASSES = {
  READY: 'vst-chip--green',
  PM: 'vst-chip--blue',
  OOS: 'vst-chip--red',
  DVIR: 'vst-chip--amber',
  PENDING_DVIR: 'vst-chip--amber',
  UNSAFE_DVIR: 'vst-chip--red',
}

const REFRESH_SECS = 60

export default {
  data() {
    return {
      // status picker popover (one at the root, position: fixed)
      editing_cell: null,
      picker_item: null,
      picker_field: null,
      picker_style: null,
      // notes editing guard + saved flash
      notes_editing: '',
      notes_saved: '',
      notes_saved_timer: null,
      // auto-refresh
      auto_refresh: true,
      refresh_secs: REFRESH_SECS,
      refresh_timer: null,
    }
  },

  computed: {
    autoRefreshTitle() {
      return this.auto_refresh
        ? 'Auto-refresh is on, every ' + this.refresh_secs + ' seconds. Click to pause.'
        : 'Auto-refresh is paused. Click to resume.'
    },
  },

  methods: {
    statusLabel,
    washChipClass(status) {
      return WASH_CHIP_CLASSES[status] || 'vst-chip--gray'
    },
    maintChipClass(status) {
      return MAINT_CHIP_CLASSES[status] || 'vst-chip--gray'
    },

    // the dvir statuses are system-written: process_vehicle_status recomputes
    // them every ~10 minutes and would thrash any manual change, so the maint
    // control goes read-only while one is active
    maintLocked(status) {
      return status === 'PENDING_DVIR' || status === 'UNSAFE_DVIR'
    },
    // system-owned dvir states are not manually enterable either: entering
    // one would be overwritten by the same recompute that locks exits
    editableMaintOptions(options) {
      return (options || []).filter(o => !o || !this.maintLocked(o.value))
    },
    cellKey(item, field) {
      return item.name + '::' + field
    },
    // tap the chip to open the colored options as a floating popover anchored
    // to it; position: fixed so it escapes the table's scrollport
    startEdit(item, field, ev) {
      const key = this.cellKey(item, field)
      if (this.editing_cell === key) { this.cancelEdit(true); return }
      const trigger = ev && (ev.currentTarget || ev.target)
      const chip = trigger && trigger.querySelector ? trigger.querySelector('.vst-status-chip') : null
      const anchor = chip || trigger
      this._trigger_rect = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : null
      this.picker_item = item
      this.picker_field = field
      this.editing_cell = key
      // render off-screen first so the unpositioned popover never flashes
      this.picker_style = { top: '-9999px', left: '-9999px' }
      this.$nextTick(() => {
        this.positionPicker()
        document.addEventListener('mousedown', this.onOpsDocClick, true)
        // a fixed popover anchored to a scrolled chip would detach
        window.addEventListener('scroll', this.onOpsPickerScroll, true)
        window.addEventListener('resize', this.onOpsPickerScroll, true)
        const pop = this.opsPickerEl()
        const el = pop && (pop.querySelector('.vst-status-opt--current') || pop.querySelector('.vst-status-opt'))
        if (el && el.focus) el.focus()
      })
    },
    opsPickerEl() {
      const ref = this.$refs.pickerPop
      if (!ref) return null
      return ref.$el || ref
    },
    // center vertically on the trigger, line the option chips up over it,
    // clamp to the viewport
    positionPicker() {
      const rect = this._trigger_rect
      const pop = this.opsPickerEl()
      if (!rect || !pop) return
      const margin = 8
      const pw = pop.offsetWidth
      const ph = pop.offsetHeight
      let top = rect.top + rect.height / 2 - ph / 2
      if (top + ph > window.innerHeight - margin) top = window.innerHeight - margin - ph
      if (top < margin) top = margin
      const firstChip = pop.querySelector('.vst-status-chip')
      const chipOffset = firstChip ? firstChip.getBoundingClientRect().left - pop.getBoundingClientRect().left : 0
      let left = rect.left - chipOffset
      if (left + pw > window.innerWidth - margin) left = window.innerWidth - margin - pw
      if (left < margin) left = margin
      this.picker_style = { top: Math.round(top) + 'px', left: Math.round(left) + 'px' }
    },
    cancelEdit(restoreFocus = false) {
      const key = this.editing_cell
      this.editing_cell = null
      this.picker_item = null
      this.picker_field = null
      this.picker_style = null
      document.removeEventListener('mousedown', this.onOpsDocClick, true)
      window.removeEventListener('scroll', this.onOpsPickerScroll, true)
      window.removeEventListener('resize', this.onOpsPickerScroll, true)
      if (restoreFocus && key) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('[data-status-trigger="' + key + '"]')
          if (el && el.focus) el.focus()
        })
      }
    },
    onOpsDocClick(e) {
      if (e.target.closest && (e.target.closest('.vst-picker-pop') || e.target.closest('.vst-chip-btn'))) return
      this.cancelEdit()
    },
    onOpsPickerScroll() { this.cancelEdit() },
    // one tap sets + saves through the page's own endpoint plumbing
    setStatus(item, field, value) {
      this.cancelEdit(true)
      if (!value || value === item[field]) return
      this.applyStatusChange(item, field, value)
    },

    noteFocus(item) {
      // pauses the auto-refresh merge from overwriting this cell mid-edit
      this.notes_editing = item.name
    },
    flashNotesSaved(name) {
      // only release the guard if it still belongs to this row; a save
      // resolving after focus moved must not expose the newly edited note
      if (this.notes_editing === name)
        this.notes_editing = ''
      if (this.notes_saved_timer) clearTimeout(this.notes_saved_timer)
      this.notes_saved = name
      this.notes_saved_timer = setTimeout(() => { this.notes_saved = '' }, 1600)
    },

    // soft-merge a fresh roster into the current rows: reuse each existing
    // row object by name so an open history panel (_showDetails) and a
    // focused notes input survive; new rows come in as-is.
    // - fields absent from the fresh record are DELETED (a movement that
    //   ended or a note cleared elsewhere must not linger), except the
    //   client-only keys below
    // - names that appear more than once on either side (FUTURE booking
    //   rows) are never merged: reusing one object for two rows corrupts
    //   both, so those rows are replaced wholesale
    vsmMergeRows(current, results) {
      const CLIENT_KEYS = ['_showDetails', 'history', 'history_error']
      const counts = {}
      const freshCounts = {}
      current.forEach(row => { counts[row.name] = (counts[row.name] || 0) + 1 })
      ;(results || []).forEach(row => { freshCounts[row.name] = (freshCounts[row.name] || 0) + 1 })
      const prev = {}
      current.forEach(row => { prev[row.name] = row })
      return (results || []).map(incoming => {
        const existing = prev[incoming.name]
        if (!existing || counts[incoming.name] > 1 || freshCounts[incoming.name] > 1)
          return incoming
        Object.keys(existing).forEach(key => {
          if (key in incoming || CLIENT_KEYS.includes(key)) return
          if (key === 'notes' && this.notes_editing === existing.name) return
          this.$delete(existing, key)
        })
        Object.keys(incoming).forEach(key => {
          if (key === 'notes' && this.notes_editing === existing.name) return
          this.$set(existing, key, incoming[key])
        })
        return existing
      })
    },

    // periodic background reload; a hidden tab, an in-flight save, an open
    // status picker, or a note being edited all defer to the next interval
    startOpsAutoRefresh() {
      this.stopOpsAutoRefresh()
      if (this.auto_refresh && !this.standalone && this.refresh_secs > 0) {
        this.refresh_timer = setInterval(this.opsAutoRefreshTick, this.refresh_secs * 1000)
      }
    },
    stopOpsAutoRefresh() {
      if (this.refresh_timer) {
        clearInterval(this.refresh_timer)
        this.refresh_timer = null
      }
    },
    opsAutoRefreshTick() {
      if (typeof document !== 'undefined' && document.hidden) return
      if (!this.auto_refresh || this.standalone) return
      if (this.loading || this.reloading || this.updating) return
      if (this.editing_cell) return
      if (this.notes_editing) return
      this.opsSoftReload()
    },
    toggleAutoRefresh() {
      this.auto_refresh = !this.auto_refresh
      if (this.auto_refresh) {
        this.startOpsAutoRefresh()
        // resuming should feel immediate, not wait a full interval
        this.opsAutoRefreshTick()
      } else {
        this.stopOpsAutoRefresh()
      }
    },
    // a tab returning to the foreground snaps current immediately
    onOpsVisibility() {
      if (typeof document !== 'undefined' && !document.hidden) this.opsAutoRefreshTick()
    },
  },

  mounted() {
    this.startOpsAutoRefresh()
    document.addEventListener('visibilitychange', this.onOpsVisibility)
  },

  beforeDestroy() {
    this.stopOpsAutoRefresh()
    this.cancelEdit()
    document.removeEventListener('visibilitychange', this.onOpsVisibility)
    if (this.notes_saved_timer) clearTimeout(this.notes_saved_timer)
  },
}
